/**
 * The QC "Products & scores" step's pure logic: its four sub-views, the Products table's saved
 * views, sorts and row action, and the run-wide roll-ups (Top issues, issues by source) built from
 * the per-product score details the app has loaded.
 */
import type { CheckScore, ProductScore, ProductScoreDetail } from '../types/quality-scores.types';

import type { IssueSource } from './issue-source';
import { issueSource } from './issue-source';
import type { ProductIssue } from './product-issues';
import { issuesBySource, productIssues } from './product-issues';
import { customerNote, feedHasDimensions } from './product-page';
import { latestOfRow } from './product-quality';
import { isVerdictBlocked, ruleLabel } from './quality-labels';

/** Run-product statuses that leave a product unwritten — read for the "Not written" reason. */
export const NOT_WRITTEN_STATUSES = [
  'FAILED',
  'NOT_ATTEMPTED',
  'NOT_REQUESTED',
  'SKIPPED_NOT_SELECTED',
  'SKIPPED_NO_PROMPT',
];

/** The step's sub-views (`?pview=`); Products is the default. */
export const QC_SCORES_VIEWS = ['products', 'issues', 'attributes', 'scores'] as const;
export type QcScoresView = (typeof QC_SCORES_VIEWS)[number];

/** The Products table's saved views (`?psv=`). */
export const QC_SAVED_VIEWS = [
  'all',
  'failing',
  'review',
  'pass',
  'not-written',
  'qc-flagged',
] as const;
export type QcSavedView = (typeof QC_SAVED_VIEWS)[number];

export const QC_SAVED_VIEW_LABELS: Record<QcSavedView, string> = {
  all: 'All',
  failing: 'Failing',
  review: 'Review',
  pass: 'Pass',
  'not-written': 'Not written',
  'qc-flagged': 'QC flagged',
};

/** The Products table's sorts (`?psort=`); Biggest loss is the default. */
export const QC_PRODUCT_SORTS = ['loss', 'low', 'gain'] as const;
export type QcProductSort = (typeof QC_PRODUCT_SORTS)[number];

export const QC_PRODUCT_SORT_LABELS: Record<QcProductSort, string> = {
  loss: 'Biggest loss',
  low: 'Lowest after',
  gain: 'Most improved',
};

/** The Products table's state, kept in the QC run page's URL so a filtered view can be shared. */
export interface QcProductsSearch {
  psv?: QcSavedView | undefined;
  psort?: QcProductSort | undefined;
  /** Product type (detected vertical). */
  ptype?: string | undefined;
  /** Instructions version (`promptVersion`). */
  pins?: string | undefined;
  /** A failing check (from Top issues) and the field it runs on. */
  pcheck?: string | undefined;
  pfield?: string | undefined;
  /** Products with an issue from this source (from the Summary). */
  psource?: IssueSource | undefined;
  /** Product id fragment. */
  pq?: string | undefined;
}

/** What the app knows about one listed product beside its score row. */
export interface QcProductFacts {
  /** The run writes but wrote nothing for this product (read from its content rows). */
  notWritten: boolean;
  /** A HIGH QC finding on this product. */
  qcFlagged: boolean;
}

/**
 * A product's issues from its score detail, in the run's latest phase. The feed's own attributes
 * aren't loaded here, so the dimension override reads the scored dimension fields only.
 */
export function qcDetailIssues(detail: ProductScoreDetail, showPost: boolean): ProductIssue[] {
  return productIssues(detail, showPost ? 'post' : 'pre', {
    feedHasDimensions: feedHasDimensions(detail.content, undefined),
  });
}

/** Whether a row's verdict is held below its score's zone (a critical check) — "Fail · blocked". */
export function rowIsBlocked(row: ProductScore, showPost: boolean): boolean {
  const latest = latestOfRow(row, showPost);
  return isVerdictBlocked(latest.verdict, latest.score);
}

/** Whether a row belongs in a saved view. */
export function inSavedView(
  row: ProductScore,
  view: QcSavedView,
  facts: QcProductFacts,
  showPost: boolean,
): boolean {
  const verdict = latestOfRow(row, showPost).verdict;
  switch (view) {
    case 'failing':
      return verdict === 'FAIL';
    case 'review':
      return verdict === 'REVIEW';
    case 'pass':
      return verdict === 'PASS';
    case 'not-written':
      return facts.notWritten;
    case 'qc-flagged':
      return facts.qcFlagged;
    default:
      return true;
  }
}

/** Ascending compare with nulls last. */
function byScore(a: number | null, b: number | null): number {
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * Sorts the listed rows. "Biggest loss" works from the list alone (per-product points need a
 * detail read per product): blocked verdicts first, then most failing checks, then lowest score.
 */
export function sortQcProducts(
  rows: readonly ProductScore[],
  sort: QcProductSort,
  showPost: boolean,
): ProductScore[] {
  const copy = [...rows];
  const latest = (row: ProductScore) => latestOfRow(row, showPost);
  if (sort === 'low') return copy.sort((a, b) => byScore(latest(a).score, latest(b).score));
  if (sort === 'gain') return copy.sort((a, b) => byScore(b.delta, a.delta));
  return copy.sort(
    (a, b) =>
      Number(rowIsBlocked(b, showPost)) - Number(rowIsBlocked(a, showPost)) ||
      latest(b).problems - latest(a).problems ||
      byScore(latest(a).score, latest(b).score),
  );
}

/** The row's one action, by where its top issue comes from. */
export type QcProductAction = 'feed' | 'why' | 'fix' | 'open';

export const QC_PRODUCT_ACTION_LABELS: Record<QcProductAction, string> = {
  feed: 'Feed problem',
  why: 'See why',
  fix: 'Fix problem',
  open: 'Open',
};

/**
 * Which action a row offers: "See why" when nothing was written, "Feed problem" when a feed rule
 * blocks it (only the customer can fix that), "Fix problem" for any other problem, else "Open".
 */
export function qcProductAction(issue: ProductIssue | null, notWritten: boolean): QcProductAction {
  if (notWritten) return 'why';
  if (!issue) return 'open';
  if (issue.blocking && issue.source === 'feed') return 'feed';
  return 'fix';
}

/** One failing check across the run, for the Top issues view. */
export interface TopIssueRow {
  checkId: string;
  field: string;
  words: string;
  severity: string;
  /** Products failing it in the latest phase — the service's own run-wide count. */
  products: number;
  /** Of the loaded product details, how many fail it, per instructions version ('' = unknown). */
  byVersion: Map<string, number>;
  loadedProducts: number;
  /** Points lost summed over the loaded products that fail it (one decimal). */
  pointsLost: number;
  /** Average points per failing loaded product (one decimal); 0 when none is loaded. */
  pointsEach: number;
  blocking: boolean;
  /** Where the issue comes from (`issueSource` on the check and field) — picks the row's fix. */
  source: IssueSource;
  /** One failing message after writing from `/scores/checks` (`examplePostFailure`), or null. */
  example: string | null;
}

/**
 * Top issues: one row per check failing in the latest phase (from `/scores/checks`), with the
 * instructions-version split and points lost taken from the per-product issues loaded so far.
 * Blocking checks first, then most points lost, then most products.
 */
export function topIssueRows(
  checks: readonly CheckScore[],
  issuesByProduct: ReadonlyMap<string, readonly ProductIssue[]>,
  versions: ReadonlyMap<string, string>,
  showPost: boolean,
): TopIssueRow[] {
  return checks
    .filter((check) => (showPost ? check.failsPost : check.failsPre) > 0)
    .map((check) => {
      const byVersion = new Map<string, number>();
      let loadedProducts = 0;
      let points = 0;
      for (const [productId, issues] of issuesByProduct) {
        const issue = issues.find((i) => i.checkId === check.checkId && i.field === check.field);
        if (!issue) continue;
        loadedProducts += 1;
        points += issue.pointsLost;
        const version = versions.get(productId) ?? '';
        byVersion.set(version, (byVersion.get(version) ?? 0) + 1);
      }
      return {
        checkId: check.checkId,
        field: check.field,
        words: ruleLabel(check.checkId, check.field),
        severity: check.severity,
        products: showPost ? check.failsPost : check.failsPre,
        byVersion,
        loadedProducts,
        pointsLost: Math.round(points * 10) / 10,
        pointsEach: loadedProducts > 0 ? Math.round((points / loadedProducts) * 10) / 10 : 0,
        blocking: check.severity === 'CRITICAL',
        source: issueSource(check.checkId, check.field),
        example: check.examplePostFailure.trim() || null,
      };
    })
    .sort(
      (a, b) =>
        Number(b.blocking) - Number(a.blocking) ||
        b.pointsLost - a.pointsLost ||
        b.products - a.products,
    );
}

/** One issue (check on a field) within a source, across the loaded products. */
export interface SourceIssueRow {
  checkId: string;
  field: string;
  words: string;
  severity: string;
  blocking: boolean;
  productIds: string[];
  /** Instructions versions of those products, most common first. */
  versions: string[];
  pointsLost: number;
  /** "Found 312 chars · needs 500 or more" from the first product carrying it, or null. */
  measuredVsNeeded: string | null;
  /** Each product's own measured-vs-needed (for per-product notes, e.g. "Copy for customer"). */
  measuredByProduct: ReadonlyMap<string, string | null>;
}

/** Issues per source, each source's rows costliest first. */
export type IssuesBySourceTable = Record<IssueSource, SourceIssueRow[]>;

/**
 * "Issues by where they come from": every loaded product's issues grouped with `issuesBySource`,
 * then merged per check across products (product ids, versions and summed points).
 */
export function sourceIssueTable(
  issuesByProduct: ReadonlyMap<string, readonly ProductIssue[]>,
  versions: ReadonlyMap<string, string>,
): IssuesBySourceTable {
  const table: IssuesBySourceTable = { instructions: [], feed: [], attributes: [], rules: [] };
  type Working = SourceIssueRow & {
    measuredByProduct: Map<string, string | null>;
    versionCounts: Map<string, number>;
  };
  const rows = new Map<string, Working>();
  for (const [productId, issues] of issuesByProduct) {
    for (const group of issuesBySource(issues)) {
      for (const issue of group.issues) {
        const key = `${group.source}|${issue.checkId}|${issue.field}`;
        let row = rows.get(key);
        if (!row) {
          row = {
            checkId: issue.checkId,
            field: issue.field,
            words: issue.words,
            severity: issue.severity,
            blocking: issue.blocking,
            productIds: [],
            versions: [],
            pointsLost: 0,
            measuredVsNeeded: issue.measuredVsNeeded,
            measuredByProduct: new Map(),
            versionCounts: new Map(),
          };
          rows.set(key, row);
          table[group.source].push(row);
        }
        if (!row.productIds.includes(productId)) row.productIds.push(productId);
        row.measuredVsNeeded ??= issue.measuredVsNeeded;
        if (!row.measuredByProduct.has(productId)) {
          row.measuredByProduct.set(productId, issue.measuredVsNeeded);
        }
        row.pointsLost = Math.round((row.pointsLost + issue.pointsLost) * 10) / 10;
        const version = versions.get(productId);
        if (version) row.versionCounts.set(version, (row.versionCounts.get(version) ?? 0) + 1);
      }
    }
  }
  for (const row of rows.values()) {
    row.versions = [...row.versionCounts.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v);
  }
  for (const source of Object.keys(table) as IssueSource[]) {
    table[source] = table[source]
      // Copy the public fields only, so the working version tally isn't carried along.
      .map(
        (row): SourceIssueRow => ({
          checkId: row.checkId,
          field: row.field,
          words: row.words,
          severity: row.severity,
          blocking: row.blocking,
          productIds: row.productIds,
          versions: row.versions,
          pointsLost: row.pointsLost,
          measuredVsNeeded: row.measuredVsNeeded,
          measuredByProduct: row.measuredByProduct,
        }),
      )
      .sort(
        (a, b) =>
          Number(b.blocking) - Number(a.blocking) ||
          b.pointsLost - a.pointsLost ||
          b.productIds.length - a.productIds.length,
      );
  }
  return table;
}

/** Whether a product's loaded issues include one from the given source. */
export function hasIssueFrom(issues: readonly ProductIssue[] | undefined, source: IssueSource) {
  return (issues ?? []).some((issue) => issue.source === source);
}

/**
 * The plain-text note for a feed issue across its products — one `customerNote` line per product
 * (the Product page's wording) with that product's own measured value, ready to paste.
 */
export function customerNotes(
  row: Pick<SourceIssueRow, 'field' | 'words' | 'productIds' | 'measuredVsNeeded'> & {
    measuredByProduct?: ReadonlyMap<string, string | null>;
  },
  productLabel: (productId: string) => string = (productId) => productId,
): string {
  return row.productIds
    .map((productId) =>
      customerNote(productLabel(productId), {
        field: row.field,
        words: row.words,
        measuredVsNeeded: row.measuredByProduct?.get(productId) ?? row.measuredVsNeeded,
      }),
    )
    .join('\n');
}
